import type { ImportGroups } from '../../../ProductWarehouse/WarehouseImportGroups';
import { parseIndividualDelivery, parseIndividualDeliveryPrice } from "./individualDelivery";
import JSZip from "jszip";
import api from "../../../../../Component/axios_Interceptor/api";
import { directUploadFiles, DirectUploadResult, PRODUCT_IMPORTS_URL } from "../../../../../Component/tool/directUpload";
import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  EXCEL_HEADERS,
  IMAGE_NAME_DELIMITER,
  MAX_IMAGES_PER_PRODUCT,
  SUPPORTED_EXCEL_EXT,
  SUPPORTED_IMAGE_EXT,
} from "./config";
import {
  FilterTab,
  ImageEntry,
  MatchCounts,
  MatchedImage,
  MatchRow,
  MatchStatus,
  ParsedRow,
  RowImageError,
} from "./types";

/** 팝업(내부 모달) 상태 — 8종 공통 */
export interface PopupState {
  kind: string;
  title: string;
  message?: string;
  errorCount?: number; // '오류 확인' 모달 — 건수를 빨간색으로 강조 렌더
  images?: MatchedImage[]; // '사용되지 않은 이미지' 목록용
  confirmText?: string;
  cancelText?: string; // 있으면 취소 버튼 노출
  onConfirm?: () => void;
}


const H = EXCEL_HEADERS;
const extOf = (name: string) => name.split(".").pop()?.toLowerCase() ?? "";
const MIB = 1024 * 1024;
const MAX_EXCEL_BYTES = 20 * MIB;
const MAX_EXCEL_ROWS = 5000;
const MAX_EXCEL_COLUMNS = 64;
const MAX_IMAGE_BYTES = 10 * MIB;
const MAX_IMPORT_IMAGES = 500;
const MAX_IMPORT_BYTES = 1024 * MIB;
const MAX_ZIP_ENTRIES = 2000;

const waitForNextPoll = (signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal.aborted) return reject(new DOMException("작업 확인이 취소되었습니다.", "AbortError"));
  const abort = () => {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", abort);
    reject(new DOMException("작업 확인이 취소되었습니다.", "AbortError"));
  };
  const timer = window.setTimeout(() => {
    signal.removeEventListener("abort", abort);
    resolve();
  }, 1000);
  signal.addEventListener("abort", abort, { once: true });
});

const toNum = (v: unknown): number | null => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

/** 이미지 디코드 가능 여부(손상 판별) */
const checkImage = (file: File): Promise<boolean> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    img.src = url;
  });

/** zip이 섞여 있으면 풀어서 안의 이미지(jpg/png)만 File[]로 펼친다. 그 외 파일은 그대로 통과. */
const expandFiles = async (files: File[]): Promise<File[]> => {
  const out: File[] = [];
  let imageCount = 0;
  let unpackedBytes = 0;
  for (const f of files) {
    if (extOf(f.name) !== "zip") {
      if (SUPPORTED_IMAGE_EXT.includes(extOf(f.name))) {
        if (f.size > MAX_IMAGE_BYTES) throw new Error(`${f.name} 이미지 파일은 10MB 이하만 등록할 수 있습니다.`);
        if (++imageCount > MAX_IMPORT_IMAGES) throw new Error("이미지는 최대 500개까지 등록할 수 있습니다.");
        unpackedBytes += f.size;
        if (unpackedBytes > MAX_IMPORT_BYTES) throw new Error("이미지 전체 용량은 1GB 이하만 등록할 수 있습니다.");
      }
      out.push(f);
      continue;
    }
    let zip: JSZip;
    try {
      zip = await JSZip.loadAsync(f);
    } catch {
      throw new Error(f.name + " 압축 파일을 읽지 못했습니다. 확인 후 다시 업로드해주세요.");
    }
    const entries = Object.values(zip.files);
    if (entries.length > MAX_ZIP_ENTRIES) throw new Error("압축 파일에는 최대 2,000개 항목만 포함할 수 있습니다.");
    let declaredBytes = 0;
    for (const entry of entries) {
      if (entry.dir) continue;
      const declaredSize = Number((entry as typeof entry & { _data?: { uncompressedSize?: number } })._data?.uncompressedSize);
      if (!Number.isSafeInteger(declaredSize) || declaredSize < 0) throw new Error("압축 파일의 크기 정보를 확인할 수 없습니다.");
      declaredBytes += declaredSize;
      if (declaredBytes > MAX_IMPORT_BYTES) throw new Error("압축 해제 후 전체 용량은 1GB 이하만 등록할 수 있습니다.");
      if (entry.name.includes("__MACOSX")) continue; // 맥 zip 잡폴더
      const base = entry.name.split("/").pop() || entry.name;
      if (!base || base.startsWith("._") || base.startsWith(".")) continue;
      if (!SUPPORTED_IMAGE_EXT.includes(extOf(base))) continue; // 이미지만
      if (declaredSize > MAX_IMAGE_BYTES) throw new Error(`${base} 이미지 파일은 10MB 이하만 등록할 수 있습니다.`);
      if (++imageCount > MAX_IMPORT_IMAGES) throw new Error("이미지는 최대 500개까지 등록할 수 있습니다.");
      if (unpackedBytes + declaredSize > MAX_IMPORT_BYTES) throw new Error("이미지 전체 용량은 1GB 이하만 등록할 수 있습니다.");
      let blob: Blob;
      try {
        blob = await entry.async("blob");
      } catch {
        throw new Error(f.name + " 압축 파일을 읽지 못했습니다. 확인 후 다시 업로드해주세요.");
      }
      if (blob.size > MAX_IMAGE_BYTES) throw new Error(`${base} 이미지 파일은 10MB 이하만 등록할 수 있습니다.`);
      unpackedBytes += blob.size;
      if (unpackedBytes > MAX_IMPORT_BYTES) throw new Error("이미지 전체 용량은 1GB 이하만 등록할 수 있습니다.");
      out.push(
        new File([blob], base, { type: blob.type || `image/${extOf(base)}` })
      );
    }
  }
  return out;
};

const parseExcelFile = async (file: File): Promise<ParsedRow[]> => {
  if (file.size > MAX_EXCEL_BYTES) throw new Error("엑셀 파일은 20MB 이하만 등록할 수 있습니다.");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error("엑셀에서 상품 정보를 읽지 못했습니다.");
  if (ws["!ref"]) {
    const range = XLSX.utils.decode_range(ws["!ref"]);
    if (range.e.r - range.s.r > MAX_EXCEL_ROWS) throw new Error("엑셀은 최대 5,000개 상품까지 등록할 수 있습니다.");
    if (range.e.c - range.s.c + 1 > MAX_EXCEL_COLUMNS) throw new Error("엑셀은 최대 64개 열까지 등록할 수 있습니다.");
  }
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
    defval: "",
  });
  if (json.length > MAX_EXCEL_ROWS) throw new Error("엑셀은 최대 5,000개 상품까지 등록할 수 있습니다.");

  const parsed: ParsedRow[] = [];
  json.forEach((r, i) => {
    const name = String(r[H.name] ?? "").trim();
    if (!name) return; // 빈 행 스킵
    const imagesCell = String(r[H.images] ?? "").trim();
    const imageNames = imagesCell
      ? imagesCell
        .split(IMAGE_NAME_DELIMITER)
        .map((s) => s.trim())
        .filter(Boolean)
      : [];
    let individualDelivery: boolean;
    let individualDeliveryPrice: number;
    try {
      individualDelivery = parseIndividualDelivery(r[H.individualDelivery]);
      individualDeliveryPrice = parseIndividualDeliveryPrice(r[H.individualDeliveryPrice] === "" ? r["배송비"] : r[H.individualDeliveryPrice]);
    } catch (error) {
      throw new Error(`${i + 2}행: ${(error as Error).message}`);
    }
    parsed.push({
      individualDelivery, individualDeliveryPrice,
      rowIndex: i,
      productCode: String(r[H.productCode] ?? "").trim(),
      name,
      price: toNum(r[H.price]),
      stock: toNum(r[H.stock]),
      option1: String(r[H.option1] ?? "").trim(),
      option2: String(r[H.option2] ?? "").trim(),
      description: String(r[H.description] ?? "").trim(),
      imageNames,
    });
  });
  return parsed;
};

const useExcelUpload = (liveSeq: number, onSuccess: () => void, warehouse = false, groups?: ImportGroups) => {
  const busy = useRef(false);
  const pendingGroups = useRef<ImportGroups | null>(null);
  const [groupLocked, setGroupLocked] = useState(false);
  const selectionVersion = useRef(0);
  const pendingUpload = useRef<DirectUploadResult | null>(null);
  const pendingJobId = useRef<string | null>(null);
  const idempotencyKey = useRef<string | null>(null);
  const uploadAbort = useRef<AbortController | null>(null);
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [uploading, setUploading] = useState(false);
  const [excelName, setExcelName] = useState("");
  const [imageUploadNames, setImageUploadNames] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [images, setImages] = useState<Map<string, ImageEntry>>(new Map());
  const [filter, setFilter] = useState<FilterTab>("전체");
  const [popup, setPopup] = useState<PopupState | null>(null);

  const closePopup = () => setPopup(null);

  useEffect(() => () => {
    selectionVersion.current += 1;
    uploadAbort.current?.abort();
  }, []);

  const invalidatePending = () => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    pendingUpload.current = null;
    pendingJobId.current = null;
    idempotencyKey.current = null;
    pendingGroups.current = null; setGroupLocked(false);
  };

  const resetAll = () => {
    selectionVersion.current += 1;
    invalidatePending();
    busy.current = false;
    setProcessing(false);
    setUploading(false);
    setUploadProgress("");
    setImages((prev) => {
      prev.forEach((e) => URL.revokeObjectURL(e.url));
      return new Map();
    });
    setRows([]);
    setExcelName("");
    setImageUploadNames([]);
    setFilter("전체");
    setPopup(null);
  };

  // ---------------- 엑셀 ----------------
  const applyExcel = async (file: File) => {
    if (busy.current) return;
    invalidatePending();
    busy.current = true;
    setProcessing(true);
    const version = selectionVersion.current;
    try {
    const parsed = await parseExcelFile(file);
    if (version !== selectionVersion.current) return;
    // 헤더(양식)가 안 맞으면 "상품명" 컬럼이 안 읽혀 모든 행이 빈 행으로 스킵되고
    // parsed가 0건이 된다 — 이 경우 조용히 넘어가면 사용자는 이유를 모른 채
    // "매칭 결과 0"만 보게 되므로, 명시적으로 서식 확인을 안내한다.
    if (parsed.length === 0) {
      setPopup({
        kind: "excelFormat",
        title: "양식을 확인해주세요",
        message:
          "엑셀에서 상품 정보를 읽지 못했습니다.\n'상품명' 등 필수 헤더가 정해진 양식과 일치하는지 확인 후 다시 업로드해주세요.",
        confirmText: "확인",
      });
      return; // 등록 중단
    }
    // 상품당 이미지 수 초과 검사 — 하나라도 초과면 엑셀 자체를 등록하지 않음
    const overflowList = parsed.filter(
      (r) => r.imageNames.length > MAX_IMAGES_PER_PRODUCT
    );
    if (overflowList.length) {
      const names = Array.from(new Set(overflowList.map((r) => r.name)));
      const listText = names.map((n) => `• ${n}`).join("\n");
      setPopup({
        kind: "overflow",
        title: "이미지 수 초과",
        message: `다음 상품의 이미지 수가 초과되었습니다.\n한 상품에 최대 ${MAX_IMAGES_PER_PRODUCT}장까지 등록가능합니다.\n\n${listText}\n\n엑셀을 수정 후 다시 업로드해주세요.`,
        confirmText: "확인",
      });
      return; // 등록 중단 (매칭 진행 안 함)
    }
    setExcelName(file.name);
    setRows(parsed);
    } catch (error) {
      if (version === selectionVersion.current) {
        setPopup({ kind: "excelFormat", title: "엑셀을 읽지 못했습니다", message: error instanceof Error && error.message ? error.message : "파일이 손상되지 않았는지 확인 후 다시 업로드해주세요.", confirmText: "확인" });
      }
    } finally {
      if (version === selectionVersion.current) {
        busy.current = false;
        setProcessing(false);
      }
    }
  };

  const onSelectExcel = (file: File) => {
    if (busy.current) return;
    if (!SUPPORTED_EXCEL_EXT.includes(extOf(file.name))) {
      setPopup({
        kind: "excelExt",
        title: "지원하지 않은 양식",
        message: "xlsx, xls 파일만 등록가능합니다.\n파일 확장자를 확인해주세요.",
        confirmText: "확인",
      });
      return;
    }
    if (excelName) {
      // 이미 엑셀이 있으면 재업로드 초기화 경고
      setPopup({
        kind: "excelReupload",
        title: "주의",
        message: "엑셀을 재업로드시 초기화됩니다.\n계속 진행하시겠습니까?",
        confirmText: "확인",
        cancelText: "취소",
        onConfirm: () => {
          resetAll();
          applyExcel(file);
        },
      });
      return;
    }
    applyExcel(file);
  };

  const onDeleteExcel = () => {
    if (busy.current) return;
    setPopup({
      kind: "excelDelete",
      title: "주의",
      message: "엑셀을 삭제시 초기화됩니다.\n계속 진행하시겠습니까?",
      confirmText: "확인",
      cancelText: "취소",
      onConfirm: resetAll,
    });
  };

  // ---------------- 이미지 ----------------
  const overwriteImages = (files: File[]) => {
    invalidatePending();
    setImages((prev) => {
      const next = new Map(prev);
      files.forEach((f) => {
        const old = next.get(f.name);
        if (old) URL.revokeObjectURL(old.url);
        next.set(f.name, {
          file: f,
          url: URL.createObjectURL(f),
          corrupt: false,
        });
      });
      return next;
    });
  };

  const addImages = async (rawFiles: File[]) => {
    if (busy.current) return;
    invalidatePending();
    busy.current = true;
    setProcessing(true);
    const version = selectionVersion.current;
    try {
    if (rawFiles.reduce((total, file) => total + file.size, 0) > MAX_IMPORT_BYTES) {
      throw new Error("선택한 파일의 전체 용량은 1GB 이하만 등록할 수 있습니다.");
    }
    // zip이 섞여 있으면 먼저 풀어서 안의 이미지를 펼친다
    const files = await expandFiles(rawFiles);
    if (version !== selectionVersion.current) return;

    const projectedImages = new Map(Array.from(images, ([name, entry]) => [name, entry.file.size]));
    files.forEach((file) => {
      if (SUPPORTED_IMAGE_EXT.includes(extOf(file.name))) projectedImages.set(file.name, file.size);
    });
    if (projectedImages.size > MAX_IMPORT_IMAGES) throw new Error("이미지는 최대 500개까지 등록할 수 있습니다.");
    if (Array.from(projectedImages.values()).reduce((total, size) => total + size, 0) > MAX_IMPORT_BYTES) {
      throw new Error("이미지 전체 용량은 1GB 이하만 등록할 수 있습니다.");
    }

    const unsupported: string[] = [];
    const corrupt: string[] = [];
    const duplicated: File[] = [];
    const fresh: { name: string; file: File }[] = [];

    for (const file of files) {
      if (!SUPPORTED_IMAGE_EXT.includes(extOf(file.name))) {
        unsupported.push(file.name);
        continue;
      }
      const ok = await checkImage(file);
      if (version !== selectionVersion.current) return;
      if (!ok) {
        corrupt.push(file.name);
        continue;
      }
      if (images.has(file.name)) {
        duplicated.push(file); // 덮어쓰기 확인 대상
        continue;
      }
      fresh.push({
        name: file.name,
        file,
      });
    }

    if (fresh.length) {
      setImages((prev) => {
        const next = new Map(prev);
        fresh.forEach(({ name, file }) => {
          const old = next.get(name);
          if (old) URL.revokeObjectURL(old.url);
          next.set(name, { file, url: URL.createObjectURL(file), corrupt: false });
        });
        return next;
      });
    }

    if (fresh.length || duplicated.length) {
      setFilter("전체");
      setImageUploadNames((prev) => Array.from(new Set([
        ...prev,
        ...rawFiles.filter((file) => extOf(file.name) === "zip" || SUPPORTED_IMAGE_EXT.includes(extOf(file.name))).map((file) => file.name),
      ])));
    }

    // 이슈 팝업 (우선순위 하나만 — 미지원 > 손상 > 중복)
    if (unsupported.length) {
      setPopup({
        kind: "unsupported",
        title: "지원하지 않은 양식",
        message: "jpg, png 파일만 등록가능합니다.\n파일 확장자를 확인해주세요.",
        confirmText: "확인",
      });
    } else if (corrupt.length) {
      setPopup({
        kind: "corrupt",
        title: "이미지 손상",
        message: `${corrupt[0]} 이(가) 손상되었습니다.\n확인 후 재업로드해주세요.`,
        confirmText: "확인",
      });
    } else if (duplicated.length) {
      setPopup({
        kind: "duplicate",
        title: "파일명 중복",
        message: `${duplicated[0].name} 이(가) 이미 존재합니다.\n기존 파일을 덮어쓸까요?`,
        confirmText: "확인",
        cancelText: "아니요",
        onConfirm: () => overwriteImages(duplicated),
      });
    }
    } catch (error: any) {
      if (version === selectionVersion.current) {
        setPopup({ kind: "imageError", title: "이미지를 읽지 못했습니다", message: error.message || "이미지 파일을 확인 후 다시 업로드해주세요.", confirmText: "확인" });
      }
    } finally {
      if (version === selectionVersion.current) {
        busy.current = false;
        setProcessing(false);
      }
    }
  };

  const removeImage = (name: string) => {
    if (busy.current) return;
    invalidatePending();
    setImageUploadNames(previous => previous.filter(fileName => fileName !== name));
    setImages((prev) => {
      const next = new Map(prev);
      const old = next.get(name);
      if (old) URL.revokeObjectURL(old.url);
      next.delete(name);
      return next;
    });
  };

  // ---------------- 매칭 ----------------
  const allMatchRows: MatchRow[] = useMemo(() => {
    return rows.map((row) => {
      const matched: MatchedImage[] = [];
      const errors: RowImageError[] = [];
      row.imageNames.forEach((imgName) => {
        if (!SUPPORTED_IMAGE_EXT.includes(extOf(imgName))) {
          errors.push({
            fileName: imgName,
            kind: "unsupported",
            message: "지원하지 않는 형식이 있습니다. (지원가능 형식 : jpg,png)",
          });
          return;
        }
        const entry = images.get(imgName);
        if (!entry) {
          errors.push({
            fileName: imgName,
            kind: "missing",
            message: `${imgName} 파일이 존재하지 않습니다.`,
          });
        } else if (entry.corrupt) {
          errors.push({
            fileName: imgName,
            kind: "corrupt",
            message: `${imgName} 파일이 손상되었습니다.`,
          });
        } else {
          matched.push({ name: imgName, url: entry.url });
        }
      });
      const status: MatchStatus =
        row.imageNames.length === 0
          ? "없음"
          : errors.length > 0
            ? "오류"
            : "성공";
      return { row, matched, errors, status };
    });
  }, [rows, images]);

  const excelNameSet = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.imageNames.forEach((n) => s.add(n)));
    return s;
  }, [rows]);

  const counts: MatchCounts = useMemo(() => {
    let matched = 0;
    images.forEach((_e, name) => {
      if (excelNameSet.has(name)) matched += 1;
    });
    return {
      totalImages: images.size,
      matched,
      unused: images.size - matched,
    };
  }, [images, excelNameSet]);

  const unusedImages: MatchedImage[] = useMemo(() => {
    const list: MatchedImage[] = [];
    images.forEach((e, name) => {
      if (!excelNameSet.has(name)) list.push({ name, url: e.url });
    });
    return list;
  }, [images, excelNameSet]);

  const tabCounts = useMemo(
    () => ({
      성공: allMatchRows.filter((m) => m.status === "성공").length,
      없음: allMatchRows.filter((m) => m.status === "없음").length,
      오류: allMatchRows.filter((m) => m.status === "오류").length,
    }),
    [allMatchRows]
  );

  const filteredRows = useMemo(
    () =>
      filter === "전체"
        ? allMatchRows
        : allMatchRows.filter((m) => m.status === filter),
    [allMatchRows, filter]
  );

  // ---------------- 팝업 액션 ----------------
  const showUnused = () => {
    if (!unusedImages.length) return;
    setPopup({
      kind: "unused",
      title: "사용되지 않은 이미지",
      message: "엑셀 리스트에 없는 이미지입니다.",
      images: unusedImages,
      confirmText: "확인",
    });
  };

  const doUpload = async () => {
    if (busy.current) return;
    const valid = allMatchRows.filter(m => m.status !== "오류").map(m => m.row);
    if ((!warehouse && !liveSeq) || !valid.length || valid.some(r => r.price === null || !Number.isInteger(r.price) || r.price < 0 || r.price > 2147483647 || r.stock === null || !Number.isInteger(r.stock) || r.stock < 0 || r.stock > 2147483647)) {
      alert("방송과 상품의 가격·재고를 확인해주세요."); return;
    }
    if (warehouse && !pendingGroups.current && groups?.newGroupName !== undefined && !groups.newGroupName.trim()) {
      alert('새 그룹 이름을 입력해주세요.'); return;
    }
    busy.current = true;
    setUploading(true);
    const version = selectionVersion.current;
    uploadAbort.current = new AbortController();
    const signal = uploadAbort.current.signal;
    try {
      const names = Array.from(new Set(valid.flatMap(r => r.imageNames)));
      for (const name of names) {
        if (!images.get(name)) throw new Error(name + " 이미지가 누락되었습니다.");
      }

      if (!pendingUpload.current) {
        const sheet = XLSX.utils.json_to_sheet(valid.map(r => ({
          상품번호: r.productCode, 상품명: r.name, 가격: r.price, 재고: r.stock,
          옵션사용여부: r.option1 ? "y" : "n", 옵션1: r.option1, 옵션2: r.option2,
          옵션재고: r.stock, 상품간략설명: r.description, 이미지: r.imageNames.join(","),
          개별배송: r.individualDelivery ? "Y" : "N",
          개별배송비: r.individualDeliveryPrice ?? 0,
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, "상품");
        const excel = new File(
          [XLSX.write(workbook, { type: "array", bookType: "xlsx" })],
          "products.xlsx",
          { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
        );
        const imageFiles = names.map(name => {
          const source = images.get(name)!.file;
          const type = extOf(name) === "png" ? "image/png" : "image/jpeg";
          return new File([source], name, { type });
        });
        pendingUpload.current = await directUploadFiles([excel, ...imageFiles], {
          purpose: warehouse ? "warehouse-import" : "product-import",
          liveSeq,
          signal,
          onProgress: progress => {
            if (version === selectionVersion.current) {
              setUploadProgress(`파일 전송 중 (${progress.percent}%)`);
            }
          },
        });
      }
      if (version !== selectionVersion.current) return;

      const uploaded = pendingUpload.current;
      if (!uploaded || uploaded.objects.length !== names.length + 1) throw new Error("업로드 결과를 확인할 수 없습니다.");
      if (warehouse && !pendingGroups.current) {
        if (groups?.newGroupName !== undefined && !groups.newGroupName.trim()) throw new Error('새 그룹 이름을 입력해주세요.');
        pendingGroups.current = { groupSeqs: [...(groups?.groupSeqs || [])], ...(groups?.newGroupName !== undefined ? { newGroupName: groups.newGroupName.trim() } : {}) };
        setGroupLocked(true);
      }
      idempotencyKey.current ??= crypto.randomUUID();
      if (!pendingJobId.current) {
        setUploadProgress("상품 등록 요청 중…");
        const accepted = await api.post(PRODUCT_IMPORTS_URL, {
          liveSeq,
          uploadSessionId: uploaded.sessionId,
          excelObjectId: uploaded.objects[0].objectId,
          imageObjectIdsByName: Object.fromEntries(names.map((name, index) => [name, uploaded.objects[index + 1].objectId])),
          idempotencyKey: idempotencyKey.current,
          ...(warehouse ? pendingGroups.current : {}),
        }, { headers: { "Content-Type": "application/json" }, signal });
        if (typeof accepted.data?.jobId !== "string") throw new Error("대량 등록 작업 번호를 확인할 수 없습니다.");
        pendingJobId.current = accepted.data.jobId;
      }

      let pollCount = 0;
      while (version === selectionVersion.current) {
        if (++pollCount > 600) throw new Error("상품 등록 처리 시간이 초과되었습니다. 잠시 후 다시 확인해주세요.");
        const statusResponse = await api.get(
          `${PRODUCT_IMPORTS_URL}/${encodeURIComponent(pendingJobId.current!)}`,
          { signal },
        );
        const job = statusResponse.data;
        const processed = Number(job?.progress?.processedRows ?? 0);
        const reportedTotal = Number(job?.progress?.totalRows);
        const total = Number.isInteger(reportedTotal) && reportedTotal > 0 ? reportedTotal : valid.length;
        setUploadProgress(`상품 행 처리 ${processed}/${total}`);
        if (job?.status === "completed") {
          const rowErrors = Array.isArray(job.errors) ? job.errors : [];
          if (rowErrors.length) {
            alert(["상품 등록이 완료되었지만 일부 행은 처리하지 못했습니다.", ...rowErrors.map((error: { row?: number; message: string }) => `${error.row ? `${error.row}행: ` : ""}${error.message}`)].join("\n"));
          } else {
            alert(`상품 행 처리 ${processed}/${total} 완료\n입력 ${rows.length}행 · 등록 완료 ${processed}행 · 제외 ${rows.length - valid.length}행\n이미지 매칭 ${names.length}개`);
          }
          resetAll();
          onSuccess();
          return;
        }
        if (job?.status === "failed") {
          const rowErrors = Array.isArray(job.errors) ? job.errors : [];
          // A retry reuses the same upload session and idempotency key. The
          // backend can then requeue this failed job without creating another.
          pendingJobId.current = null;
          throw new Error(rowErrors.length
            ? rowErrors.map((error: { row?: number; message: string }) => `${error.row ? `${error.row}행: ` : ""}${error.message}`).join("\n")
            : job.message || "상품 등록에 실패했습니다.");
        }
        if (!['queued', 'processing'].includes(job?.status)) throw new Error("대량 등록 상태를 확인할 수 없습니다.");
        await waitForNextPoll(signal);
      }
    } catch (error: any) {
      if (version === selectionVersion.current && error?.name !== "AbortError") {
        alert(error?.response?.data?.message || error.message || "상품 등록에 실패했습니다.");
      }
    } finally {
      if (version === selectionVersion.current) {
        uploadAbort.current = null;
        busy.current = false;
        setUploading(false);
        setUploadProgress("");
      }
    }
  };

  const onComplete = () => {
    if (busy.current) return;
    const errorCount = allMatchRows.filter((m) => m.status === "오류").length;
    if (errorCount > 0) {
      setPopup({
        kind: "ignoreErrors",
        title: "오류 확인",
        errorCount,
        confirmText: "확인",
        cancelText: "취소",
        onConfirm: doUpload,
      });
      return;
    }
    doUpload();
  };

  return {
    groupLocked,
    excelName,
    imageUploadNames,
    imageFileNames: Array.from(images.keys()),
    uploading,
    processing,
    uploadProgress,
    filter,
    setFilter,
    rows,
    matchRows: filteredRows,
    counts,
    tabCounts,
    unusedImages,
    popup,
    closePopup,
    hasExcel: !!excelName,
    actions: {
      onSelectExcel,
      onDeleteExcel,
      addImages,
      removeImage,
      showUnused,
      onComplete,
      resetAll,
    },
  };
};

export default useExcelUpload;
